// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

// Leaflet site map — background tile layer only, no internal chrome.
// UI controls (aerial/street toggle, lock button, address label) live in the
// OpenEstimate logistics toolbar so they sit alongside the other tool buttons.

import React, { useEffect, useRef } from "react";
import { feetPerPixelFrom } from "../lib/mapScale";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// ---------------------------------------------------------------------------
// Tile sources — no API keys required
// ---------------------------------------------------------------------------
const TILES = {
  aerial: {
    url:  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attr: "Tiles &copy; Esri — Source: Esri, USGS, NOAA",
  },
  street: {
    url:  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
    attr: "Tiles &copy; Esri — Esri, DeLorme, NAVTEQ, TomTom",
  },
} as const;

export type MapViewMode = keyof typeof TILES;

interface Props {
  /** Full project address — used first for precise geocoding */
  address:   string;
  /** City+ST+ZIP fallback query from AddressFields */
  geoQuery?: string;
  viewMode:  MapViewMode;
  /** When true: all map interaction is disabled so the SVG overlay can capture events */
  locked:    boolean;
  T:         any;
  /** Fires with a short display label when the map successfully locates the address */
  onLocated?: (label: string) => void;
  /**
   * Fires with the ground scale, in feet per pixel, whenever the view changes.
   *
   * A drawing laid over the map can only be measured if it knows this, and
   * the map is the one thing that knows it exactly — asking somebody to
   * calibrate an aerial by eye is asking them to re-measure what the
   * projection already determines.
   */
  onScale?: (feetPerPixel: number | null) => void;
}

export default function SiteMap({ address, geoQuery, viewMode, locked, T, onLocated, onScale }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef       = useRef<L.Map | null>(null);
  const layerRef     = useRef<L.TileLayer | null>(null);
  const markerRef    = useRef<L.CircleMarker | null>(null);
  const lastGeoRef   = useRef<string>("");
  const abortRef     = useRef<AbortController | null>(null);
  const debounceRef  = useRef<ReturnType<typeof setTimeout>>();
  // Held in a ref so the map is bound once: a parent that re-creates the
  // callback would otherwise tear the listeners down and put them back on
  // every render.
  const onScaleRef   = useRef(onScale);
  onScaleRef.current = onScale;

  // ── Init map ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    // React StrictMode double-invokes effects: cleanup runs map.remove() but
    // Leaflet can leave _leaflet_id on the DOM node, causing "already initialized"
    // on the second mount. Wipe it before every init so L.map() starts clean.
    delete (containerRef.current as any)._leaflet_id;

    const map = L.map(containerRef.current, {
      center:             [38.9, -77.0],
      zoom:               4,
      zoomControl:        true,
      attributionControl: true,
    });

    const layer = L.tileLayer(TILES[viewMode].url, {
      attribution: TILES[viewMode].attr,
      maxZoom: 20,
    });
    layer.addTo(map);
    layerRef.current = layer;
    mapRef.current   = map;

    // ── The map's own scale ──
    // Measured rather than derived from the zoom level, so it holds for
    // whatever projection the map is in: take a hundred pixels across the
    // middle of the view and ask the map how far that is on the ground.
    const report = () => {
      const m = mapRef.current;
      const tell = onScaleRef.current;
      if (!m || !tell) return;
      try {
        const p = m.latLngToContainerPoint(m.getCenter());
        const a = m.containerPointToLatLng(p);
        const b = m.containerPointToLatLng(L.point(p.x + 100, p.y));
        tell(feetPerPixelFrom(a.distanceTo(b) / 100));
      } catch {
        tell(null);   // mid-teardown; the next move will say
      }
    };
    map.on("moveend", report);
    map.on("zoomend", report);
    map.on("resize", report);
    report();

    return () => {
      map.off("moveend", report);
      map.off("zoomend", report);
      map.off("resize", report);
      map.remove();
      mapRef.current    = null;
      layerRef.current  = null;
      markerRef.current = null;
    };
    // intentionally omit viewMode from deps — handled in its own effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Swap tile layer when viewMode changes ────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    layerRef.current?.remove();
    const layer = L.tileLayer(TILES[viewMode].url, {
      attribution: TILES[viewMode].attr,
      maxZoom: 20,
    });
    layer.addTo(map);
    layerRef.current = layer;
  }, [viewMode]);

  // ── Enable / disable interaction when locked prop changes ────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (locked) {
      map.dragging.disable();
      map.scrollWheelZoom.disable();
      map.doubleClickZoom.disable();
      map.touchZoom.disable();
      map.boxZoom.disable();
      map.keyboard.disable();
    } else {
      map.dragging.enable();
      map.scrollWheelZoom.enable();
      map.doubleClickZoom.enable();
      map.touchZoom.enable();
      map.boxZoom.enable();
      map.keyboard.enable();
    }
  }, [locked]);

  // ── Geocode and fly to address ───────────────────────────────────────────
  useEffect(() => {
    // Always prefer the full address for street-level accuracy.
    // geoQuery (city+ST+ZIP) is a fallback if the street address is still partial.
    const effective = address.trim() || (geoQuery ?? "").trim();
    if (!effective) return;

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (effective === lastGeoRef.current) return;

      abortRef.current?.abort();
      abortRef.current    = new AbortController();
      lastGeoRef.current  = effective;

      const url =
        `https://nominatim.openstreetmap.org/search` +
        `?q=${encodeURIComponent(effective)}&format=json&limit=1&countrycodes=us&email=openestimate%40app`;

      fetch(url, {
        signal:  abortRef.current.signal,
        headers: { Accept: "application/json" },
      })
        .then(r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then((data: any[]) => {
          if (!data || data.length === 0) return;

          const item = data[0];
          const lat  = parseFloat(item.lat);
          const lon  = parseFloat(item.lon);
          const map  = mapRef.current;
          if (!map || isNaN(lat) || isNaN(lon)) return;

          if (item.boundingbox) {
            const [s, n, w, e] = (item.boundingbox as string[]).map(parseFloat);
            const latSpan = n - s;
            const lonSpan = e - w;

            if (latSpan < 0.004 && lonSpan < 0.006) {
              // Parcel / building level — zoom right in
              map.setView([lat, lon], 18, { animate: true });
            } else if (latSpan < 0.04 && lonSpan < 0.06) {
              // Street / neighbourhood level
              map.setView([lat, lon], 16, { animate: true });
            } else {
              // City / region — fit with modest padding
              const latPad = latSpan * 0.4;
              const lonPad = lonSpan * 0.4;
              map.fitBounds(
                [[s - latPad, w - lonPad], [n + latPad, e + lonPad]],
                { maxZoom: 17, animate: true }
              );
            }
          } else {
            map.setView([lat, lon], 17, { animate: true });
          }

          // Subtle pin
          if (markerRef.current) {
            markerRef.current.setLatLng([lat, lon]);
          } else {
            markerRef.current = L.circleMarker([lat, lon], {
              radius:      8,
              color:       "#ffffff",
              weight:      2,
              fillColor:   "#4a90e2",
              fillOpacity: 0.85,
            }).addTo(map);
          }

          const label = (item.display_name as string)
            .replace(/,?\s*United States\s*$/i, "")
            .replace(/,?\s*USA\s*$/i, "");
          onLocated?.(label);
        })
        .catch((err: Error) => {
          if (err.name === "AbortError") return;
          onLocated?.("");
        });
    }, 900);

    return () => { clearTimeout(debounceRef.current); };
  }, [address, geoQuery, onLocated]);

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height: "100%", display: "block" }}
    />
  );
}
